import { Instagram, Linkedin, MapPin, ArrowUpRight } from "lucide-react";
import { LOGO_URL, CONTACT } from "@/lib/site-config";
import { useT } from "@/i18n/LangProvider";
import { useQuoteHref } from "@/i18n/routing";

export default function Footer() {
  const { t } = useT();
  const quoteHref = useQuoteHref();

  const navItems = [
    ["nav.offer", "#oferta"],
    ["nav.geo", "#geo"],
    ["nav.process", "#processo"],
    ["nav.portfolio", "#portfolio"],
    ["nav.faq", "#faq"],
  ];

  return (
    <footer
      data-testid="site-footer"
      className="px-6 md:px-8 py-16"
      style={{
        background: "#050912",
        borderTop: "1px solid rgba(184,150,46,0.15)",
      }}
    >
      <div className="section-inner">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-12">
          <div>
            <img
              src={LOGO_URL}
              alt="Be Oasis · Web Agency"
              className="h-12 w-auto mb-4"
              draggable="false"
            />
            <p className="font-sans-brand text-sm text-[color:var(--muted-white)] leading-relaxed max-w-xs mb-6">
              {t("footer.about")}
            </p>

            <div className="flex items-start gap-2" data-testid="footer-location">
              <MapPin
                className="w-4 h-4 text-[color:var(--gold-light)] mt-0.5 flex-shrink-0"
                strokeWidth={1.6}
              />
              <div>
                <p className="font-mono-brand text-[0.62rem] text-[color:var(--gold-light)] mb-1">
                  {t("footer.location_label")}
                </p>
                <p className="font-sans-brand text-sm text-[color:var(--white)]">
                  {t("footer.location_value")}
                </p>
              </div>
            </div>
          </div>

          <div>
            <p className="font-mono-brand text-xs text-[color:var(--gold-light)] mb-4">
              {t("footer.nav")}
            </p>
            <ul className="space-y-2 font-sans-brand text-sm text-[color:var(--muted-white)]">
              {navItems.map(([key, href]) => (
                <li key={key}>
                  <a
                    href={href}
                    className="hover:text-[color:var(--gold-light)] transition-colors"
                    data-testid={`footer-link-${key}`}
                  >
                    {t(key)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-mono-brand text-xs text-[color:var(--gold-light)] mb-4">
              {t("footer.contact")}
            </p>
            <p
              className="font-sans-brand text-sm text-[color:var(--muted-white)] leading-relaxed mb-5"
              data-testid="footer-contact-note"
            >
              {t("footer.contact_note")}
            </p>

            <a
              href={quoteHref}
              className="inline-flex items-center gap-2 font-sans-brand text-sm font-medium text-[color:var(--white)] border border-[color:var(--gold)] hover:bg-[rgba(184,150,46,0.12)] transition-colors px-4 py-2.5 rounded-full"
              data-testid="footer-quote-cta"
            >
              {t("footer.contact_cta")}
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
            </a>

            <div className="flex gap-4 mt-6">
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 inline-flex items-center justify-center border border-[rgba(184,150,46,0.3)] text-[color:var(--gold-light)] hover:bg-[rgba(184,150,46,0.1)] transition-colors"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a
                href={CONTACT.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 inline-flex items-center justify-center border border-[rgba(184,150,46,0.3)] text-[color:var(--gold-light)] hover:bg-[rgba(184,150,46,0.1)] transition-colors"
                data-testid="footer-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>

        <div className="divider-gold mb-8" />

        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <p
            className="font-mono-brand text-[0.65rem] text-[color:var(--muted-white)]"
            data-testid="footer-copyright"
          >
            {t("footer.copyright")}
          </p>
          <p className="font-mono-brand text-[0.65rem] text-[color:var(--muted-white)]">
            {t("footer.caption")}
          </p>
        </div>
      </div>
    </footer>
  );
}
